import React, { useEffect, useState, useCallback } from 'react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Activity, RefreshCw, Loader2, AlertCircle, CheckCircle, Clock, Search } from 'lucide-react';
import { toast } from 'sonner';
import { getSyncHealth, syncShipmentNow } from '../lib/api';

const formatRelative = (hours) => {
  if (hours === null || hours === undefined) return 'Nunca';
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  return `${days}d ${Math.round(hours - days * 24)}h`;
};

const StatusPill = ({ row }) => {
  if (row.sync_in_progress) {
    return (
      <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/30 gap-1" data-testid={`sync-status-${row.mbl}`}>
        <Loader2 size={12} className="animate-spin" /> Sincronizando
      </Badge>
    );
  }
  if (row.last_synced_at === null) {
    return (
      <Badge className="bg-red-500/20 text-red-400 border-red-500/30 gap-1" data-testid={`sync-status-${row.mbl}`}>
        <AlertCircle size={12} /> Nunca
      </Badge>
    );
  }
  if (row.stale) {
    return (
      <Badge className="bg-amber-500/20 text-amber-400 border-amber-500/30 gap-1" data-testid={`sync-status-${row.mbl}`}>
        <Clock size={12} /> Atrasado
      </Badge>
    );
  }
  return (
    <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 gap-1" data-testid={`sync-status-${row.mbl}`}>
      <CheckCircle size={12} /> OK
    </Badge>
  );
};

export function SyncHealthDashboard() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ total_shipments: 0, stale_count: 0, never_synced_count: 0, shipments: [] });
  const [syncingMbl, setSyncingMbl] = useState(null);
  const [search, setSearch] = useState('');
  const [threshold, setThreshold] = useState(24);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getSyncHealth({ hours_threshold: threshold, limit: 200 });
      setData(res.data);
    } catch (e) {
      toast.error('Error al cargar sync health');
    } finally {
      setLoading(false);
    }
  }, [threshold]);

  useEffect(() => { load(); }, [load]);

  const handleSync = async (mbl) => {
    setSyncingMbl(mbl);
    const id = toast.loading(`Sincronizando ${mbl}... (continúa en segundo plano)`);
    try {
      await syncShipmentNow(mbl);
      toast.success(`${mbl}: sync iniciado. Se actualizará al completarse.`, { id, duration: 4000 });
      // Refresh list and start polling for this specific MBL
      await load();
      // Poll every 10s for up to 3 minutes to detect completion
      let attempts = 0;
      const interval = setInterval(async () => {
        attempts += 1;
        try {
          const res = await getSyncHealth({ hours_threshold: threshold, limit: 200 });
          setData(res.data);
          const row = res.data.shipments.find((r) => r.mbl === mbl);
          if (!row || !row.sync_in_progress) {
            clearInterval(interval);
            setSyncingMbl(null);
          }
        } catch {
          /* ignore */
        }
        if (attempts > 18) {
          clearInterval(interval);
          setSyncingMbl(null);
        }
      }, 10000);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al sincronizar', { id });
      setSyncingMbl(null);
    }
  };

  const filtered = search
    ? data.shipments.filter(s =>
        s.mbl.toLowerCase().includes(search.toLowerCase()) ||
        (s.organization_name || '').toLowerCase().includes(search.toLowerCase()) ||
        (s.client_name || '').toLowerCase().includes(search.toLowerCase())
      )
    : data.shipments;

  return (
    <div className="space-y-5" data-testid="sync-health-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-semibold flex items-center gap-2">
            <Activity className="h-5 w-5 text-primary" />
            Sync Health
          </h2>
          <p className="text-sm text-muted-foreground">
            Detecta embarques cuyo tracking se está quedando atrás para soporte proactivo.
          </p>
        </div>
        <Button onClick={load} disabled={loading} variant="outline" size="sm" data-testid="sync-health-refresh">
          {loading ? <Loader2 size={14} className="animate-spin mr-2" /> : <RefreshCw size={14} className="mr-2" />}
          Refrescar
        </Button>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="border border-border/60 rounded-md p-4 bg-card/40">
          <div className="text-xs text-muted-foreground uppercase tracking-wider">Embarques activos</div>
          <div className="text-2xl font-semibold mt-1">{data.total_shipments}</div>
        </div>
        <div className="border border-red-500/30 rounded-md p-4 bg-red-500/5">
          <div className="text-xs text-red-400 uppercase tracking-wider">Nunca sincronizados</div>
          <div className="text-2xl font-semibold mt-1 text-red-400">{data.never_synced_count}</div>
        </div>
        <div className="border border-amber-500/30 rounded-md p-4 bg-amber-500/5">
          <div className="text-xs text-amber-400 uppercase tracking-wider">Atrasados (&gt; {threshold}h)</div>
          <div className="text-2xl font-semibold mt-1 text-amber-400">{data.stale_count - data.never_synced_count}</div>
        </div>
        <div className="border border-emerald-500/30 rounded-md p-4 bg-emerald-500/5">
          <div className="text-xs text-emerald-400 uppercase tracking-wider">Sanos</div>
          <div className="text-2xl font-semibold mt-1 text-emerald-400">{data.total_shipments - data.stale_count}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por MBL, organización o cliente"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="sync-health-search"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground">Umbral de alerta:</span>
          <select
            value={threshold}
            onChange={(e) => setThreshold(Number(e.target.value))}
            className="bg-background border border-border/60 rounded px-2 py-1 text-sm"
            data-testid="sync-health-threshold"
          >
            <option value={12}>12h</option>
            <option value={24}>24h</option>
            <option value={48}>48h</option>
            <option value={72}>72h</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="border border-border/60 rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted/30 text-muted-foreground">
              <tr>
                <th className="text-left py-2 px-3 font-medium">MBL</th>
                <th className="text-left py-2 px-3 font-medium">Organización</th>
                <th className="text-left py-2 px-3 font-medium">Cliente</th>
                <th className="text-left py-2 px-3 font-medium">Contenedores</th>
                <th className="text-left py-2 px-3 font-medium">Estado sync</th>
                <th className="text-left py-2 px-3 font-medium">Hace</th>
                <th className="text-right py-2 px-3 font-medium">Acción</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="text-center py-12 text-muted-foreground"><Loader2 className="animate-spin inline mr-2" size={14} /> Cargando...</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} className="text-center py-12 text-muted-foreground">Sin resultados</td></tr>
              ) : (
                filtered.map((row) => (
                  <tr
                    key={row.mbl}
                    className="border-t border-border/40 hover:bg-accent/20 transition-colors"
                    data-testid={`sync-health-row-${row.mbl}`}
                  >
                    <td className="py-2 px-3 font-mono text-xs">
                      <a href={`/shipments/${encodeURIComponent(row.mbl)}`} className="hover:text-primary hover:underline">
                        {row.mbl}
                      </a>
                    </td>
                    <td className="py-2 px-3 text-xs">{row.organization_name}</td>
                    <td className="py-2 px-3 text-xs">{row.client_name}</td>
                    <td className="py-2 px-3 text-xs text-center">{row.container_count}</td>
                    <td className="py-2 px-3"><StatusPill row={row} /></td>
                    <td className="py-2 px-3 text-xs text-muted-foreground">{formatRelative(row.hours_since_sync)}</td>
                    <td className="py-2 px-3 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSync(row.mbl)}
                        disabled={syncingMbl === row.mbl}
                        data-testid={`sync-health-action-${row.mbl}`}
                      >
                        {syncingMbl === row.mbl ? (
                          <Loader2 size={12} className="animate-spin" />
                        ) : (
                          <><RefreshCw size={12} className="mr-1" /> Re-sync</>
                        )}
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
